import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ServerList from '../../components/ServerList';
import ServerForm from '../../components/ServerForm';
import ServerDialog from '../../components/ServerDialog';
import BannerAdmin from '../../components/BannerAdmin';
import IikoCloudAdmin from '../../components/IikoCloudAdmin';
import UserActivityAdmin from '../../components/UserActivityAdmin';
import AccountCard from '../../components/AccountCard';
import NotificationSettingsCard from '../../components/notifications/NotificationSettingsCard';
import InstallAppCard from '../../components/InstallAppCard';
import FeedbackCard from '../../components/FeedbackCard';
import { fetchServers, fetchActiveServer, setActiveServer, createServer, updateServer, deleteServer } from '../../services/serverService';
import { extractErrorMessage } from '../../utils/errors';
import { isTelegramMiniApp } from '../../utils/platform';

// Порт по умолчанию 443 (HTTPS) — как у облачных серверов iiko
const EMPTY_SERVER_DATA = { ip_address: '', port: '443', login: '', password: '' };

function getToken() {
  return (
    localStorage.getItem('token') ||
    localStorage.getItem('access_token') ||
    sessionStorage.getItem('token') ||
    sessionStorage.getItem('access_token') ||
    null
  );
}

async function waitForToken(maxMs = 3000, stepMs = 150) {
  const attempts = Math.ceil(maxMs / stepMs);
  for (let i = 0; i < attempts; i++) {
    const t = getToken();
    if (t) return t;
    await new Promise(r => setTimeout(r, stepMs));
  }
  return null;
}

function Profile({ isAdmin = false, email = '', username = '', emailVerified, createdAt, buildVersion = '' }) {
  const [servers, setServers] = useState([]);
  const [activeServer, setActiveServerState] = useState(null);
  const [showAddServerDialog, setShowAddServerDialog] = useState(false);
  const [showServerDialog, setShowServerDialog] = useState(false);
  // 'create' — новый сервер, 'edit' — открыт из окна сервера кнопкой «Редактировать»
  const [serverFormMode, setServerFormMode] = useState('create');
  const [serverData, setServerData] = useState(EMPTY_SERVER_DATA);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authToken, setAuthToken] = useState(getToken());

  // Если токен появится позже (после авторизации), перезапускаем инициализацию
  useEffect(() => {
    const onStorage = () => {
      if (!loading && !getToken()) return;
      initializeData();
    };
    const onCustom = () => {
      if (!loading && !getToken()) return;
      initializeData();
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('auth-token-updated', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('auth-token-updated', onCustom);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeData = async () => {
    setLoading(true);
    setError(null);

    let token = getToken();
    if (!token) {
      console.warn('⏳ Токен не найден сразу, ожидаем появления…');
      token = await waitForToken(3000, 150);
    }

    if (!token) {
      console.error('❌ Токен не найден даже после ожидания — требуется авторизация.');
      if (isTelegramMiniApp()) {
        setError('Токен не найден. Пожалуйста, перезапустите приложение через Telegram.');
      } else {
        setError('Войдите снова');
        window.location.replace('/login');
      }
      setLoading(false);
      return;
    }

    try {
      setAuthToken(token);
      const serversList = await fetchServers(token);
      setServers(serversList);

      const activeServerData = await fetchActiveServer(token);
      setActiveServerState(activeServerData || null);
      setError(null);
    } catch (err) {
      console.error('Ошибка при инициализации данных:', err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleServerClick = async (server) => {
    const token = getToken();
    if (!token) {
      setError('Токен не найден. Перезапустите приложение.');
      return;
    }

    if (activeServer?.id === server.id) {
      setServerData(server);
      setShowServerDialog(true);
    } else {
      try {
        await setActiveServer(server.id, token);
        const updatedActiveServer = await fetchActiveServer(token);
        setActiveServerState(updatedActiveServer);
        setError(null);
      } catch (err) {
        console.error('Ошибка при установке активного сервера:', err);
        setError(extractErrorMessage(err));
      }
    }
  };

  // Действия над сервером («⋯» в строке): информация, редактирование, удаление
  const handleServerMenu = (server) => {
    setServerData(server);
    setShowServerDialog(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setServerData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const token = getToken();
    if (!token) {
      setError('Токен не найден. Перезапустите приложение.');
      return;
    }

    try {
      if (serverFormMode === 'edit') {
        // Обновляем ту же запись, а не создаём новую. Бэкенд сам обновит кеш активного сервера
        const { id, ip_address, port, login, password } = serverData;
        await updateServer(id, { ip_address, port: Number(port), login, password }, token);
        setShowAddServerDialog(false);
        setServerData(EMPTY_SERVER_DATA);

        const serversList = await fetchServers(token);
        setServers(serversList);
        if (activeServer?.id === id) {
          // Новые адрес/логин в localStorage и в шапке с выбором зала
          setActiveServerState(await fetchActiveServer(token));
        }
        setError(null);
        return;
      }

      const newServer = await createServer(serverData, token);
      setShowAddServerDialog(false);
      setServerData(EMPTY_SERVER_DATA);

      const serversList = await fetchServers(token);
      setServers(serversList);

      await setActiveServer(newServer.id, token);
      setActiveServerState(newServer);

      setError(null);
    } catch (err) {
      console.error('Ошибка при сохранении сервера:', err);
      setError(extractErrorMessage(err));
    }
  };

  const handleCancel = () => {
    setShowAddServerDialog(false);
    setServerData(EMPTY_SERVER_DATA);
  };

  const handleDelete = async (serverId) => {
    const token = getToken();
    if (!token) {
      setError('Токен не найден. Перезапустите приложение.');
      return;
    }

    try {
      await deleteServer(serverId, token);
      const serversList = await fetchServers(token);
      setServers(serversList);

      // Удалили активный: бэкенд убрал его из кеша, но другой сервер сам не выберет
      if (activeServer?.id === serverId) {
        const [nextServer] = Array.isArray(serversList) ? serversList : [];
        if (nextServer) {
          await setActiveServer(nextServer.id, token);
          setActiveServerState(await fetchActiveServer(token));
        } else {
          localStorage.removeItem('activeServer');
          localStorage.removeItem('selectedGroup');
          window.dispatchEvent(new CustomEvent('activeServerChanged'));
          setActiveServerState(null);
        }
      }

      setShowServerDialog(false);
      setError(null);
    } catch (err) {
      console.error('Ошибка при удалении сервера:', err);
      setError(extractErrorMessage(err));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  const cardSx = {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '14px',
    p: 1.8,
    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
    height: '100%',
    boxSizing: 'border-box',
  };

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} alignItems={{ xs: 'flex-start', md: 'stretch' }}>
        <Grid item xs={12} md={isTelegramMiniApp() === false ? 8 : 12} sx={{ display: { md: 'flex' } }}>
          <Box sx={{ ...cardSx, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, minHeight: { md: 40 } }}>
              <Typography sx={{ fontWeight: 600 }}>Серверы iiko</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={() => {
                  // Без сброса форма открылась бы с данными сервера, который смотрели последним
                  setServerData(EMPTY_SERVER_DATA);
                  setServerFormMode('create');
                  setShowAddServerDialog(true);
                }}
                sx={{
                  height: 36,
                  px: 1.5,
                  borderRadius: '10px',
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  color: '#e4e4e7',
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', boxShadow: 'none' },
                }}
              >
                Добавить
              </Button>
            </Box>
            <ServerList
              servers={servers}
              activeServer={activeServer}
              onServerClick={handleServerClick}
              onServerMenu={handleServerMenu}
            />
          </Box>
        </Grid>

        {isTelegramMiniApp() === false && (
          <Grid item xs={12} md={4} sx={{ display: { md: 'flex' } }}>
            <AccountCard
              email={email}
              emailVerified={emailVerified}
              createdAt={createdAt}
              sx={{ ...cardSx, width: '100%' }}
            />
          </Grid>
        )}

        {/* Сама скрывается в Telegram, в установленном приложении и после установки */}
        <InstallAppCard sx={cardSx} />

        <Grid item xs={12}>
          {/* key — перечитать список серверов в настройках после добавления/удаления сервера */}
          <NotificationSettingsCard key={servers.map((s) => s.id).join(',')} sx={cardSx} />
        </Grid>

        <Grid item xs={12}>
          <FeedbackCard
            token={authToken}
            email={email}
            username={username}
            buildVersion={buildVersion}
            serverName={activeServer?.server_name}
            sx={cardSx}
          />
        </Grid>

        {isAdmin && (
          <Grid item xs={12}>
            <BannerAdmin token={authToken} sx={cardSx} />
          </Grid>
        )}

        {isAdmin && (
          <Grid item xs={12}>
            <IikoCloudAdmin token={authToken} sx={cardSx} />
          </Grid>
        )}

        {isAdmin && (
          <Grid item xs={12}>
            <UserActivityAdmin token={authToken} sx={cardSx} />
          </Grid>
        )}
      </Grid>

      {buildVersion && (
        <Typography sx={{ mt: 4, textAlign: 'center', color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem' }}>
          Версия {buildVersion}
        </Typography>
      )}

      {/* Диалог для добавления сервера: по центру на десктопе, лист снизу на телефоне */}
      <ServerForm
        open={showAddServerDialog}
        serverData={serverData}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        onCancel={handleCancel}
        mode={serverFormMode}
      />

      {/* Диалог с информацией о сервере */}
      <ServerDialog
        server={serverData}
        active={activeServer?.id === serverData.id}
        open={showServerDialog}
        onClose={() => setShowServerDialog(false)}
        onEdit={() => {
          // Пароль API не отдаёт — поле пустое, его нужно ввести заново
          setServerData((prev) => ({ ...prev, password: '' }));
          setServerFormMode('edit');
          setShowServerDialog(false);
          setShowAddServerDialog(true);
        }}
        onDelete={() => handleDelete(serverData.id)}
      />

    </Box>
  );
}

export default Profile;
